import React, { useMemo } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import classNames from 'classnames';
import {
  Typography,
  Tooltip,
	CircularProgress
} from '@material-ui/core';
import TransactionEventHistoryGrid from 'components/underwrite/shared/histories/TransactionEventHistoryGrid';
import PropertyHistoryOpportunitiesGrid from 'components/underwrite/acquisition/propertyUnderwrite/header/propertyHistory/PropertyHistoryOpportunitiesGrid';
import PropertyHistoryValuationHistoryGrid from 'components/underwrite/acquisition/propertyUnderwrite/header/propertyHistory/PropertyHistoryValuationHistoryGrid';
import { Warning as WarningIcon } from "@material-ui/icons";
import { LoadingOverlayWrapper } from 'components/layout';
import { setSelectedOpportunityHistoryTab } from 'store/actions';

const excludedReasons = ['Below % of List Threshold']; // Exclude no bid reason when value is Below % of List

const hasNoBidReason = opp => {
  return opp.noBidReason != null && !excludedReasons.includes(opp.noBidReason);
}

const hasDoNotBuyWarning = opp => {
  return opp.crmTerminationReason === 'Do Not Buy';
}

const hasHighFidelityFieldsWarning = (opp, currentOppId) => {
  if (opp.id === currentOppId) return false;
  //return opp.crmProposedRepairBidAmount != null || opp.sellerHoa != null || opp.sellerTaxes != null;
  return opp.crmProposedRepairBidAmount != null || opp.sellerHoa != null; //EF 20250721 temp removal of seller taxes
}

const PropertyHistoryTabs = ({ classes, txnOppID, transactionEvents, opportunities, valuations, isFetchingTransactionEvents,
	isFetchingPropertyHistoryOpportunities, isFetchingValuationHistory, selectedOpportunityHistoryTab, setSelectedOpportunityHistoryTab }) => {
	const getTitleColorClass = useMemo(() => {
		if (!opportunities) return null;
		if (opportunities.some(opp => hasDoNotBuyWarning(opp))) { // RED (Do Not Buy) takes absolute precedence
			// if (selectedOpportunityHistoryTab !== 'opportunities') {
			// 	setSelectedOpportunityHistoryTab('opportunities');
			// }
			return classes.doNotBuyWarning;
		} else if (opportunities.some(opp => hasNoBidReason(opp))) { // if not RED, check YELLOW (No Bid Reason)
			// if (selectedOpportunityHistoryTab !== 'opportunities') {
			// 	setSelectedOpportunityHistoryTab('opportunities');
			// }
			return classes.noBidReason;
		} else if (opportunities.some(opp => hasHighFidelityFieldsWarning(opp, txnOppID))) { // if neither RED nor YELLOW, check BLUE (High Fidelity)
			return classes.highFidelityFieldsWarning;
		} else return null;
	}, [opportunities, txnOppID]);

	const getWarningIcons = () => {
		const icons = [];
		if (!opportunities) return icons;
		if (opportunities.some(opp => hasDoNotBuyWarning(opp))) {
			icons.push(
				<Tooltip title='Do Not Buy warning'>
					<WarningIcon fontSize='small' className={classNames(classes.warningIcon, classes.doNotBuyWarning)} />
				</Tooltip>
			);
		};
		if (opportunities.some(opp => hasNoBidReason(opp))) {
			icons.push(
				<Tooltip title='No Bid Reason warning'>
					<WarningIcon fontSize='small' className={classNames(classes.warningIcon, classes.noBidReason)} />
				</Tooltip>
			);
		};
		if (opportunities.some(opp => hasHighFidelityFieldsWarning(opp, txnOppID))) {
			icons.push(
				<Tooltip title='Historical fields available'>
					<WarningIcon fontSize='small' className={classNames(classes.warningIcon, classes.highFidelityFieldsWarning)} />
				</Tooltip>
			);
		};
		return icons;
	}

	const valuationsTitle = `Valuation History${valuations ? ` (${valuations.length})` : ''}`; //this will be the new tab to add
	const opportunitiesTitle = `Opportunity History${opportunities ? ` (${opportunities.length})` : ''}`;
	const transactionEventsTitle = `Transaction History${transactionEvents ? ` (${transactionEvents.length})` : ''}`;
  return (
		<div>
			<div className={classes.tabContainer}>
				<div className={classNames(classes.tab, selectedOpportunityHistoryTab === 'valuations' && classes.selectedTab)} onClick={() => setSelectedOpportunityHistoryTab('valuations')}>
					<Typography className={classes.title} variant="subtitle2">{valuationsTitle}</Typography>
					{isFetchingValuationHistory &&
					<CircularProgress size={16} style={{ marginLeft: 8 }}/>
					}
				</div>
				<div className={classNames(classes.tab, selectedOpportunityHistoryTab === 'opportunities' && classes.selectedTab)} style={{ display: 'flex' }} onClick={() => setSelectedOpportunityHistoryTab('opportunities')}>
					<Typography className={classNames(classes.title, getTitleColorClass)} variant="subtitle2">{opportunitiesTitle}</Typography>
					{getWarningIcons().map(icon => icon)}
					{isFetchingPropertyHistoryOpportunities &&
						<CircularProgress size={16} style={{ marginLeft: 8 }}/>
					}
				</div>
				<div className={classNames(classes.tab, selectedOpportunityHistoryTab === 'transactionEvents' && classes.selectedTab)} onClick={() => setSelectedOpportunityHistoryTab('transactionEvents')}>
					<Typography className={classes.title} variant="subtitle2">{transactionEventsTitle}</Typography>
					{isFetchingTransactionEvents &&
						<CircularProgress size={16} style={{ marginLeft: 8 }}/>
					}
				</div>
			</div>
			<div style={{ height: 152, margin: '0 8px 8px' }}>
			{
				selectedOpportunityHistoryTab === 'valuations'
				? <LoadingOverlayWrapper
					active={isFetchingValuationHistory}
					message={'Fetching valuations...'}
					style={{ height: '100%' }}
				>
					<PropertyHistoryValuationHistoryGrid valuations={valuations} />
					</LoadingOverlayWrapper>
				: selectedOpportunityHistoryTab === 'opportunities'
				? <LoadingOverlayWrapper
					active={isFetchingPropertyHistoryOpportunities}
					message={'Fetching opportunities...'}
					style={{ height: '100%' }}
					>
					<PropertyHistoryOpportunitiesGrid opportunities={opportunities} txnOppID={txnOppID} />
					</LoadingOverlayWrapper>
				: <LoadingOverlayWrapper
					active={isFetchingTransactionEvents}
					message={'Fetching transaction events...'}
					style={{ height: '100%' }}
					>
					<TransactionEventHistoryGrid gudTransactionEvents={transactionEvents} />
					</LoadingOverlayWrapper>
			}
			</div>
		</div>
  )
};

const styles = theme => ({
	tabContainer: {
		display: 'flex',
		marginLeft: 8,
		// marginBottom: 6
	},
	tab: {
		border: '1px solid #e0e0e0',
		borderBottom: 0,
		borderRadius: '8px 8px 0 0',
		padding: '2px 8px',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center'
	},
	selectedTab: {
		backgroundColor: '#e1f2fb'
	},
  title: {
    // marginBottom: 6,
    // marginLeft: 8
  },
	warningIcon: {
		marginLeft: 6
	},
  noBidReason: {
    color: theme.palette.warning.text,
    fontWeight: 'bold'
  },
  doNotBuyWarning: {
    color: theme.palette.red.dark,
    fontWeight: 'bold'
  },
  highFidelityFieldsWarning: {
    color: "#5baeff",
    fontWeight: 'bold'
  }
})

const mapStateToProps = (state) => {
	return {
	  isFetchingTransactionEvents: state.propertyUWState.isFetchingTransactionEvents,
	  isFetchingPropertyHistoryOpportunities: state.propertyUWState.isFetchingPropertyHistoryOpportunities,	  
	  isFetchingValuationHistory: state.propertyUWState.isFetchingValuationHistory,
		selectedOpportunityHistoryTab: state.propertyUWState.selectedOpportunityHistoryTab
	}
};

const mapDispatchToProps = {
	setSelectedOpportunityHistoryTab
}

export default compose(
	withStyles(styles),
	connect(mapStateToProps, mapDispatchToProps)
)(PropertyHistoryTabs);
